import type { PerformanceMonitorService } from './performance-monitor';
import type { APITiming } from '../types';

export class APIInterceptor {
  private readonly originalFetch: typeof fetch;
  private isInstalled = false;

  constructor(private readonly performanceMonitor: PerformanceMonitorService) {
    this.originalFetch = window.fetch;
  }

  install() {
    if (this.isInstalled) return;
    this.isInstalled = true;

    this.interceptFetch();
  }

  private interceptFetch() {
    const monitor = this.performanceMonitor;
    const originalFetch = this.originalFetch;

    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const startTime = performance.now();
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const method = init?.method || 'GET';

      let response: Response | undefined;
      let status: number | undefined;

      try {
        response = await originalFetch(input, init);
        status = response.status;
        return response;
      } catch (e) {
        throw e;
      } finally {
        const endTime = performance.now();
        const duration = endTime - startTime;

        const isGraphQL =
          url.includes('/graphql') || url.includes('/api/graphql');
        const type = isGraphQL ? 'graphql' : 'fetch';

        const timing: APITiming = {
          url,
          method,
          startTime,
          duration,
          status,
          type,
          size: response?.headers?.get('content-length')
            ? parseInt(response.headers.get('content-length')!, 10)
            : undefined,
        };

        monitor.recordAPITiming(timing);
      }
    };
  }

  monitorWebSocket(socket: WebSocket, name: string = 'websocket') {
    if (!this.performanceMonitor) return socket;

    const monitor = this.performanceMonitor;
    let messageCount = 0;

    const connectStart = performance.now();
    monitor.mark(`${name}:connect:start`);
    let connectionEstablished = 0;
    let firstDataReceived = 0;

    socket.addEventListener('open', () => {
      connectionEstablished = performance.now() - connectStart;
      monitor.mark(`${name}:connected`);
      monitor.measure(
        `${name}:connection`,
        `${name}:connect:start`,
        `${name}:connected`
      );
    });

    const originalSend = socket.send.bind(socket);
    socket.send = function (
      data: string | ArrayBufferLike | Blob | ArrayBufferView
    ) {
      const sendStart = performance.now();
      originalSend(data);
      const sendDuration = performance.now() - sendStart;

      monitor.recordAPITiming({
        url: socket.url,
        method: 'SEND',
        startTime: sendStart,
        duration: sendDuration,
        type: 'websocket',
        size: typeof data === 'string' ? data.length : undefined,
      });
    };

    socket.addEventListener('message', event => {
      messageCount++;

      if (messageCount === 1) {
        firstDataReceived = performance.now() - connectStart;
        monitor.mark(`${name}:firstMessage`);
        monitor.measure(
          `${name}:firstMessage`,
          `${name}:connect:start`,
          `${name}:firstMessage`
        );

        monitor.setWebSocketMetrics({
          connectionStart: connectStart,
          connectionEstablished,
          firstDataReceived,
        });
      }

      const size = typeof event.data === 'string' ? event.data.length : 0;

      monitor.recordAPITiming({
        url: socket.url,
        method: 'RECEIVE',
        startTime: performance.now(),
        duration: 0,
        type: 'websocket',
        size,
      });
    });

    return socket;
  }

  uninstall() {
    if (!this.isInstalled) return;

    window.fetch = this.originalFetch;
    this.isInstalled = false;
  }
}

let globalInterceptor: APIInterceptor | null = null;

export function installAPIInterceptor(
  performanceMonitor: PerformanceMonitorService
): APIInterceptor {
  if (globalInterceptor) {
    return globalInterceptor;
  }

  globalInterceptor = new APIInterceptor(performanceMonitor);
  globalInterceptor.install();

  return globalInterceptor;
}

export function uninstallAPIInterceptor() {
  if (globalInterceptor) {
    globalInterceptor.uninstall();
    globalInterceptor = null;
  }
}

export function getAPIInterceptor(): APIInterceptor | null {
  return globalInterceptor;
}
